import React, { useRef, useState } from 'react';
import { BannerAlert, Text } from '@amzn/storm-ui';
import { bannerMetricsPublisher, PartitionedBanners } from '../types';
import { BANNER_LEVEL, BANNER_TYPE } from 'src/app/slice';
import { BannersModal } from '../Common/BannersModal';
import { logWithTakt } from 'src/commons/utils/logWithTakt';
import { ConnectedBannerTriggeredCarouselModal } from '../Common/BannerTriggeredCarouselModal';
import { BannerText, StyledAlert, StyledLink } from '../styles';
import {
  useHeaderBundle,
  usePageHeaderBundle,
} from 'src/providers/PantherLocalizationProvider';
import { BannerLink } from 'src/commons/components/ConsolidatedBanner/styles';

interface ConsolidatedBannerProps {
  partitionedBanners: PartitionedBanners;
  onBannerDismiss: () => void;
  bannerLevel: BANNER_LEVEL;
}

export const ConsolidatedBanner: React.VFC<ConsolidatedBannerProps> = ({
  partitionedBanners,
  onBannerDismiss,
  bannerLevel,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [canDismissBanner, setCanDismissBanner] = useState(false);
  const modalTrigger = useRef(null);
  const pageHeaderTranslation = usePageHeaderBundle();
  const headerTranslation = useHeaderBundle();
  const bannerType =
    partitionedBanners.error.length > 0
      ? BANNER_TYPE.ERROR
      : partitionedBanners.warning.length > 0
        ? BANNER_TYPE.WARNING
        : BANNER_TYPE.INFO;
  const bannerText = pageHeaderTranslation
    .getMessage('page-header-banner-experiencing-issues-text')
    ?.replace('${x}', partitionedBanners.total.toString());
  const isConsoleLevel = bannerLevel === BANNER_LEVEL.CONSOLE;

  const carouselModalBanner = partitionedBanners.info.find(
    (banner) => banner.modal !== undefined,
  );
  const learnMoreAriaLabel = headerTranslation
    .getMessage('console-banner-learn-more-multiple-messages-aria-label')
    ?.replace('${x}', partitionedBanners.total.toString());

  const toggleModal = (event) => {
    event?.preventDefault();
    const nextIsModalOpen = !isModalOpen;
    if (nextIsModalOpen) {
      const bannerLevelWithFirstLetterCapitalized =
        bannerLevel.charAt(0).toUpperCase() + bannerLevel.slice(1);
      bannerMetricsPublisher[bannerLevel].publishCounterMonitor(
        `bannerLinkClicked:Consolidated${bannerLevelWithFirstLetterCapitalized}LevelBanner`,
        1,
      );
      logWithTakt(`${bannerLevel}BannerClicked`, {
        bannerId: `Consolidated${bannerLevelWithFirstLetterCapitalized}Banner`,
      });
    }

    if (
      !canDismissBanner &&
      nextIsModalOpen &&
      bannerType !== BANNER_TYPE.ERROR
    ) {
      setCanDismissBanner(true);
    }
    setIsModalOpen(nextIsModalOpen);
  };

  return (
    <>
      {isConsoleLevel ? (
        <BannerAlert
          data-test-id="consolidated-banners"
          variant={bannerType}
          withCloseButton={canDismissBanner}
          onClose={onBannerDismiss}
        >
          {isConsoleLevel && <BannerText>{bannerText} </BannerText>}
          <BannerLink
            href="#"
            onClick={toggleModal}
            aria-label={learnMoreAriaLabel}
            isRenderedInModal={false}
          >
            {pageHeaderTranslation.getMessage(
              'page-header-banner-learn-more-text',
            )}
          </BannerLink>
        </BannerAlert>
      ) : (
        <StyledAlert
          data-test-id="consolidated-banners"
          variant={bannerType}
          withCloseButton={canDismissBanner}
          onClose={onBannerDismiss}
          header={bannerText}
        >
          {isConsoleLevel && <Text inline>{bannerText} </Text>}
          <StyledLink
            href="#"
            onClick={toggleModal}
            aria-label={learnMoreAriaLabel}
          >
            {pageHeaderTranslation.getMessage(
              'page-header-banner-learn-more-text',
            )}
          </StyledLink>
        </StyledAlert>
      )}
      <BannersModal
        partitionedBanners={partitionedBanners}
        header={bannerText}
        isModalOpen={isModalOpen}
        onClose={toggleModal}
        bannerLevel={bannerLevel}
      />
      {carouselModalBanner && (
        <ConnectedBannerTriggeredCarouselModal
          banner={carouselModalBanner}
          modalTrigger={modalTrigger}
        />
      )}
    </>
  );
};
